import { postCvcMatrixGenerate } from '@/api';
import { ReactComponent as MatrixIcon } from '@/assets/icons/MatrixIcon.svg';
import { ReactComponent as MoveToIcon } from '@/assets/icons/MoveToIcon.svg';
import Button from '@/components/Button/Button';
import useJobSubmit from '@/hooks/useJobSubmit';
import { WebSocketContext } from '@/providers/WebSocketProvider';
import {
  generateMatrixModelSelector,
  generateSourceListSelector,
  generateTargetListSelector,
  selectedGeneratedInterpolationMapSelector,
} from '@/stores/cvc';
import { Secondary } from '@/styles/Colors';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { CardUIContent, CardUIHeader } from '../../../components/CardUI';
import Title from '../../../components/Title/Title';
import {
  isInterpolationSelector,
  parametersValueSelector,
  selectedCvcTypeSelector,
} from '../../../stores/models';
import useCvcModelState from '../hooks/useCvcModelState';
import useGenerate from '../hooks/useGenerate';
import GenerateList from './GenerateList';
import GenerateMatrix from './GenerateMatrix';

type GeneratePanelMode = 'GRID' | 'LIST';
const GeneratePanel: React.FC<{ resizeDep: number }> = ({ resizeDep }) => {
  const { t } = useTranslation();
  const generateSourceList = useRecoilValue(generateSourceListSelector);
  const generateTargetList = useRecoilValue(generateTargetListSelector);
  const isInterpolation = useRecoilValue(isInterpolationSelector);
  const matrix = useRecoilValue(generateMatrixModelSelector);
  const [mode, setMode] = useState<GeneratePanelMode>('LIST');
  const { isSourceOnly } = useCvcModelState();
  const selectedGenerateSourceList = generateSourceList.filter(
    (target) => target.isChecked
  );
  const selectedGenerateTargetList = generateTargetList.filter(
    (target) => target.isChecked
  );

  const selectedGenerateInterpolationMap = useRecoilValue(
    selectedGeneratedInterpolationMapSelector
  );
  const generateFileCount = useMemo(() => {
    if (isSourceOnly) {
      return selectedGenerateSourceList.length;
    }
    if (isInterpolation) {
      if (mode === 'LIST') {
        const groupSet = new Set<string>();
        // interpolation 의 경우 check 된 item의 groupName이 1개 이상인지 체크
        for (let i = 0; i < generateTargetList.length; i++) {
          generateTargetList[i].isChecked &&
            generateTargetList[i].groupName &&
            groupSet.add(generateTargetList[i].groupName as string);
          if (groupSet.size > 1) {
            break;
          }
        }
        // group이 2개 미만일 경우 generate disable
        if (groupSet.size < 2) {
          groupSet.clear();
          return 0;
        } else {
          groupSet.clear();
          return (
            selectedGenerateSourceList.length *
            selectedGenerateInterpolationMap.GroupB.length *
            selectedGenerateInterpolationMap.GroupA.length
          );
        }
      } else {
        // matrix mode 일 경우 matrix에서 true 인 count를 찾아 반환
        return Object.values(matrix).filter((item) => item).length;
      }
    }
    // interpolation 이 아닌 경우list mode일 경우, 체크된 아이템의 합집합
    if (mode === 'LIST') {
      return (
        selectedGenerateSourceList.length * selectedGenerateTargetList.length
      );
    } else {
      // matrix mode 일 경우 matrix에서 true 인 count를 찾아 반환
      return Object.values(matrix).filter((item) => item).length;
    }
  }, [
    isSourceOnly,
    generateTargetList,
    selectedGenerateSourceList,
    selectedGenerateTargetList,
    selectedGenerateInterpolationMap,
    mode,
    matrix,
    isInterpolation,
  ]);
  // Job events are handled route-independently by JobEventsBridge in
  // App.tsx (ISSUE-030) — no page-level useProcessingEvents here.
  const { sessionId } = useContext(WebSocketContext);

  const parameters = useRecoilValue(parametersValueSelector);
  const modelType = useRecoilValue(selectedCvcTypeSelector);
  const { generateList, updateJobIds } = useGenerate();
  const submitJob = useJobSubmit();
  const generate = useCallback(async () => {
    if (mode === 'LIST') {
      // useGenerate already routes its three branches through submitJob.
      await generateList(
        selectedGenerateSourceList,
        selectedGenerateTargetList,
        selectedGenerateInterpolationMap
      );
    } else {
      // GRID is dispatched here rather than in useGenerate, so it needs its
      // own guard. postCvcMatrixGenerate also THROWS on an unparseable cell
      // key (ISSUE-068) — that rejection lands here too, which is why the
      // acknowledgement is worded for "could not start" rather than for a
      // server error specifically.
      const jobIds = await submitJob(sessionId as string, () =>
        postCvcMatrixGenerate(
          modelType,
          sessionId as string,
          matrix,
          parameters
        )
      );
      if (!jobIds) return;
      updateJobIds(jobIds);
    }
  }, [
    matrix,
    modelType,
    selectedGenerateInterpolationMap,
    mode,
    sessionId,
    parameters,
    selectedGenerateSourceList,
    selectedGenerateTargetList,
    updateJobIds,
    generateList,
    submitJob,
  ]);

  useEffect(() => {
    // Reads the SELECTOR, not a second string test (ISSUE-069). This was
    // `modelType.endsWith('-interpolation')` while `isInterpolationSelector`
    // tests `endsWith('interpolation')` -- no hyphen. Both read the same value
    // and agreed only by coincidence of how the shipped models happen to be
    // named. A catalog entry ending in `interpolation` WITHOUT the hyphen
    // satisfied the render gates (which use the selector) and failed this
    // reset, stranding the panel in GRID with the "Matrix View" button hidden
    // by that same selector -- no way back. `public/models.json` is edited at
    // runtime by the operating organisation (NFR-019), so model names are not
    // under this repository's control.
    if ((isInterpolation || isSourceOnly) && mode !== 'LIST') {
      setMode('LIST');
    }
  }, [isInterpolation, isSourceOnly, mode]);

  return (
    <>
      <CardUIHeader>
        <Title size="lg" color={Secondary[200]}>
          {t('Generate')}
        </Title>
        <div className="generate-buttons">
          {!isSourceOnly && !isInterpolation && (
            <Button
              startIcon={<MatrixIcon />}
              className="button-view-matrix"
              onClick={() => setMode(mode === 'GRID' ? 'LIST' : 'GRID')}
              size="lg"
            >
              {mode === 'GRID' ? t('List View') : t('Matrix View')}
            </Button>
          )}
          <Button
            startIcon={<MoveToIcon />}
            className="button-gen-files"
            onClick={generate}
            disabled={!generateFileCount}
            size="lg"
          >
            {t('Generate Files', {
              count: generateFileCount,
              n: generateFileCount > 1 ? 's' : '',
            })}
          </Button>
        </div>
      </CardUIHeader>
      <CardUIContent className="sup-files generate-grid">
        {mode === 'LIST' ? (
          <>
            <GenerateList
              dimension="row"
              title="Source to Change"
              list={generateSourceList}
              resizeDep={resizeDep}
            />
            {!isSourceOnly && (
              <GenerateList
                dimension="col"
                title="Target to Mimic"
                list={generateTargetList}
                resizeDep={resizeDep}
              />
            )}
          </>
        ) : (
          <GenerateMatrix
            generateSourceList={generateSourceList}
            generateTargetList={generateTargetList}
          />
        )}
      </CardUIContent>
    </>
  );
};
export default GeneratePanel;
